import { Flex, Form, Select, SelectProps, Tag, Typography } from 'antd'
import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { MessageTemplate } from '../../../message-template'
import { TemplateBubble } from '../../template-bubble'
import { TemplateStatusBadge } from '../../template-status-badge'
import { languageOf, useCampaignLocale } from '../../../campaign-locale'

type TemplateSelectProps = {
    templates: MessageTemplate[]
} & SelectProps

export function TemplateSelect({ templates, placeholder, className, ...props }: TemplateSelectProps) {
    const { t } = useTranslation()
    const form = Form.useFormInstance()
    const language = languageOf(useCampaignLocale(form))
    const matchesLanguage = (template: MessageTemplate) => languageOf(template.language) === language
    const sortedTemplates = [...templates].sort((a, b) => Number(matchesLanguage(b)) - Number(matchesLanguage(a)))

    return (
        <Select
            {...props}
            className={clsx(
                '[&.ant-select-single]:h-auto [&_.ant-select-selector]:!h-auto [&_.ant-select-selector]:!min-h-8 [&_.ant-select-selector]:!items-start [&_.ant-select-selection-item]:!h-auto [&_.ant-select-selection-item]:!py-1 [&_.ant-select-selection-item]:!leading-normal [&_.ant-select-selection-placeholder]:!leading-[30px]',
                className
            )}
            placeholder={placeholder ?? t('Select template')}
            popupMatchSelectWidth={false}
            options={sortedTemplates.map((template) => ({
                label: <TemplatePreview template={template} />,
                value: template.id,
                template,
            }))}
            optionRender={(option) => <TemplatePreview template={option.data.template} />}
        />
    )
}

function TemplatePreview({ template }: { template: MessageTemplate }) {
    return (
        <Flex
            vertical
            gap={9}
            className="max-w-[min(640px,calc(100vw-64px))] py-1"
        >
            <Flex
                align="center"
                gap={8}
                wrap="wrap"
            >
                <Typography.Text
                    strong
                    className="text-sm"
                >
                    {template.title}
                </Typography.Text>
                <Tag className="m-0">{template.language}</Tag>
                <TemplateStatusBadge status={template.status} />
            </Flex>

            <TemplateBubble
                body={template.body}
                className="text-[13px]"
            />
        </Flex>
    )
}
